/*
  Retrieves every element from ElementContext whose `key` equals `value` and
  arranges them by order. For example, running

    useChildElements("parent_element_id", "123")

  would return an ordered list of `children` with parent element 123, as well as
  a few methods to interact with them:

    • addChild(obj, index)
    Add a child to this parent (at an optional index). For instance, to add an
    image, you could run addChild({ type, url, caption, alt }). This will
    automatically add an association with the parent, an order, name, and
    element_id. You can add multiple children by passing an array of objects
    instead of a single object.

    • removeChild(uuid)
    Pass in the UUID of any element to remove it. Removing a parent will also
    remove its children.

    • reorderChild(a, b)
    Move a child element from position `a` to position `b`. To use this, you
    must pass a `key` and `value` param to useChildElements. This will also
    update the orders of its siblings.

    • countSiblingType(type)
    Returns the number of siblings of a certain type.
*/

import { useElements } from "../data/form-data"
import { uuid } from "utils/uuid"
import { parse } from "@convertkit/editor"

export function useChildElements(key, value) {
  const { all: elements, update, remove } = useElements()

  // Finds and orders all non-deleted elements with a `key` matching `value`.
  const getChildElements = (key, value) => {
    if (!key || !value) return []
    return elements
      .filter(el => el[key] === value && !el._delete)
      .sort((a, b) => a.order - b.order)
  }

  const children = getChildElements(key, value)

  // Adds a child at position `index` and adjusts the orders of other children.
  // You can pass in one child as an object, or multiple as an array.
  const addChild = (properties, index = children.length) => {
    const newElements = [].concat(properties).map(element => {
      const element_id = uuid()
      return {
        name: element_id,
        element_id,
        [key]: value,
        order: index,
        ...element
      }
    })
    const oldElements = [...children]
    oldElements.splice(index, 0, ...newElements)
    oldElements.map((el, index) => update({ ...el, order: index }))
    return newElements
  }

  // Removes a child with `element_id` and adjusts the orders of other children.
  const removeChild = element_id => {
    const { region, parent_element_id } = elements.find(
      element => element.element_id === element_id
    )

    const siblings = region
      ? getChildElements("region", region)
      : getChildElements("parent_element_id", parent_element_id)

    let index = 0
    const newSiblings = siblings
      .map(el => {
        const isElementToDelete = el.element_id === element_id
        const isDeleted = el._delete
        if (isElementToDelete && !isDeleted) {
          remove({ name: element_id })
        } else if (!isElementToDelete) {
          const newElement = { ...el, order: index++ }
          update(newElement)
          return newElement
        }
      })
      .filter(Boolean)

    mergeTextElements(newSiblings)
  }

  // Takes an array of elements, and merges neighbors of type `Text`.
  const mergeTextElements = elements => {
    const findNextElement = index => {
      const next = elements[index + 1]
      if (!next) {
        return
      } else if (!next._delete) {
        return next
      } else if (next._delete) {
        return findNextElement(index + 1)
      }
    }

    const mergedElementIds = []
    const newElements = elements.map((current, index) => {
      const next = findNextElement(index)
      if (current.type === "Text" && next?.type === "Text") {
        mergedElementIds.push(next.element_id)
        // Focusing an empty Slate area will produce <p> tags containing a zero-
        // width space. These are "blank" paragraphs that should not be merged.
        const nextContent =
          next.content === "<p>&#8203;</p>" ? "" : next.content
        const content = `${current.content} ${nextContent}`
        const value = JSON.stringify(parse(content))
        return { ...current, content, value }
      } else {
        return current
      }
    })

    let index = 0
    newElements.map(element => {
      const { element_id } = element

      // TODO: This is a temporary workaround until we structure ContentEditor
      // to update via context instead of internal state. Once this is fixed,
      // delete the manual key assignment below. More info:
      // https://convertkit.slack.com/archives/CHZGVGC2W/p1594674451105300
      const unique = new Date().getTime()

      mergedElementIds.includes(element_id)
        ? remove({ name: element_id })
        : update({ ...element, order: index++, key: `${element_id}-${unique}` })
    })
  }

  const reorderChild = (from, to) => {
    const oldChildren = [...children]
    const [removed] = oldChildren.splice(from, 1)
    oldChildren.splice(to, 0, removed)
    oldChildren.map((child, index) => {
      update({
        ...child,
        order: index
      })
    })
    mergeTextElements(oldChildren)
  }

  const countSiblingType = type =>
    children.filter(obj => obj.type === type).length

  return { children, addChild, removeChild, reorderChild, countSiblingType }
}
